import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';

export type ColorCombination = {
  name: string;
  colors: string[];
};

export type ColorCombinationCardProps = {
  /** Combination name (e.g. "Spring Morning") */
  name: string;
  /** Array of hex color strings */
  colors: string[];
  /** Optional display name for each color (same length as colors); shown with hex when provided */
  colorNames?: (string | undefined)[];
  /** Called when user copies a single color */
  onCopyColor?: (color: string) => void;
  /** Called when user copies the whole combination */
  onCopyAll?: (text: string) => void;
  /** Optional style overrides */
  style?: object;
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (
      Platform.OS === 'web' &&
      typeof navigator !== 'undefined' &&
      navigator.clipboard
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    if (Platform.OS !== 'web') {
      try {
        const Clipboard = require('@react-native-clipboard/clipboard').default;
        if (Clipboard?.setString) {
          Clipboard.setString(text);
          return true;
        }
      } catch {
        /* ignore */
      }
    }
    return false;
  } catch {
    return false;
  }
};

/** Returns contrast text and badge overlay colors for a hex background */
const getStripColors = (hex: string): { text: string; badge: string } => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return {
    text: lum > 0.55 ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.92)',
    badge: lum > 0.55 ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.28)',
  };
};

/** Single large colour strip — fills equal width, shows name + hex at bottom, tappable */
const ColorStrip: React.FC<{
  hex: string;
  name?: string;
  onPress: (hex: string) => Promise<void>;
}> = ({ hex, name, onPress }) => {
  const [copied, setCopied] = useState(false);
  const { text: textColor, badge: badgeColor } = getStripColors(hex);

  const handlePress = async () => {
    await onPress(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  const displayLine1 = copied ? '✓ Copied' : name || hex.toUpperCase();
  const displayLine2 = name && !copied ? hex.toUpperCase() : null;

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.75}
      style={[styles.strip, { backgroundColor: hex }]}
      accessibilityLabel={
        name ? `Tap to copy ${name} ${hex}` : `Tap to copy ${hex}`
      }
      accessibilityRole="button"
    >
      <View style={[styles.hexBadge, { backgroundColor: badgeColor }]}>
        <Text style={[styles.hexText, { color: textColor }]} numberOfLines={1}>
          {displayLine1}
        </Text>
        {displayLine2 != null && (
          <Text
            style={[styles.hexTextSmall, { color: textColor }]}
            numberOfLines={1}
          >
            {displayLine2}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

/**
 * Redesigned card that displays a named colour combination as large vertical
 * strips. Each strip is tappable to copy its hex; copy-all available in header.
 * Card uses overflow:hidden so strip corners clip to card's border radius.
 */
const ColorCombinationCard: React.FC<ColorCombinationCardProps> = ({
  name,
  colors,
  colorNames,
  onCopyColor,
  onCopyAll,
  style,
}) => {
  const { colors: themeColors } = useTheme();

  const handleCopyColor = async (hex: string) => {
    if (onCopyColor) {
      onCopyColor(hex);
      return;
    }
    const ok = await copyToClipboard(hex);
    if (!ok) Alert.alert('Copy', hex);
  };

  const handleCopyAll = async () => {
    const text = `${name}: ${colors.join(', ')}`;
    if (onCopyAll) {
      onCopyAll(text);
      return;
    }
    const ok = await copyToClipboard(text);
    if (!ok) Alert.alert('Copy', text);
  };

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: themeColors.card,
          borderColor: themeColors.border,
          ...(Platform.OS === 'web'
            ? ({ boxShadow: `0 2px 16px 0 ${themeColors.text}0D` } as object)
            : {
                shadowColor: themeColors.text,
                shadowOpacity: 0.06,
                shadowRadius: 12,
                shadowOffset: { width: 0, height: 2 },
                elevation: 2,
              }),
        },
        style,
      ]}
    >
      {/* Card header: combination name + copy-all button */}
      <View style={styles.cardHeader}>
        <Text
          style={[styles.name, { color: themeColors.text }]}
          numberOfLines={1}
        >
          {name}
        </Text>
        <TouchableOpacity
          onPress={handleCopyAll}
          style={[styles.copyAllBtn, { borderColor: themeColors.border }]}
          accessibilityLabel={`Copy all colours for ${name}`}
          activeOpacity={0.7}
        >
          <Ionicons
            name="copy-outline"
            size={13}
            color={themeColors.secondary}
          />
          <Text style={[styles.copyAllText, { color: themeColors.secondary }]}>
            Copy all
          </Text>
        </TouchableOpacity>
      </View>

      {/* Large colour strips row — overflow:hidden clips corners from parent card */}
      <View style={styles.stripsRow}>
        {colors.map((hex, i) => (
          <ColorStrip
            key={`${hex}-${i}`}
            hex={hex}
            name={colorNames?.[i]}
            onPress={handleCopyColor}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  name: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: -0.2,
    flex: 1,
    marginRight: 10,
  },
  copyAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
  },
  copyAllText: {
    fontSize: 12,
    fontWeight: '600',
  },
  stripsRow: {
    flexDirection: 'row',
    height: 88,
  },
  strip: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  hexBadge: {
    paddingVertical: 6,
    paddingHorizontal: 2,
    alignItems: 'center',
  },
  hexText: {
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.2,
    textAlign: 'center',
  },
  hexTextSmall: {
    fontSize: 8,
    fontWeight: '600',
    letterSpacing: 0.2,
    textAlign: 'center',
    marginTop: 1,
  },
});

export default ColorCombinationCard;
